import utils from '@/src/utils'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import type { AppState } from '../../store'

export const TRACK_LIMIT_AMOUNT = 40 // 本地激活列表限制数量
export const TRACK_LIST_KEY = 'his_track_list' // 激活列表本地缓存key
export const TRACK_LIST_ARCHIVE_KEY = 'his_track_archive_list' // 归档列表本地缓存key

export interface Item {
  num: string
  time?: string
}

export interface UserState {
  activeList: Array<Item>, 
  activeQueryList: Array<any>
  archiveList: Array<Item>
}

const initialState: UserState = {
  activeList: [], // 激活列表
  activeQueryList: [], // 激活接口查询信息
  archiveList: [] // 归档列表
}

const setLocalStorage = (key: string, data: Array<Item>) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(data))
  } catch (error) {
    console.log(error)
  }
}

// 校验数据长度
export const checkActiveListLimit = (list:Array<Item>, num: string) => {
  if (list.find((item: any) => item.num === num)) return false
  if (list.length >= TRACK_LIMIT_AMOUNT) {
    // const err = utils.tools.getErrorDesc(=$t, '-11010105', TRACK_LIMIT_AMOUNT)
    return {
      code: '-11010105',
      limit: TRACK_LIMIT_AMOUNT
    }
  }
  return false
} 

// 去重
const clearRepatData = (list: Array<Item>) => {
  const arr: any = []
  list.forEach((item) => {
    if (!arr.find((aItem: any) => aItem.num === item.num )) {
      arr.push(item)
    }
  })
  return arr
}

export const trackLocalSlice = createSlice({
  name: 'counter',
  initialState,
  reducers: {
    // 获取当前本地缓存数据
    initLocalData: (state) => {
      const activeList = JSON.parse(localStorage.getItem(TRACK_LIST_KEY) || '[]')
      const archiveList = JSON.parse(localStorage.getItem(TRACK_LIST_ARCHIVE_KEY) || '[]')

      //  ****** 兼容老版本数据 Start ************* 
      // 是否同步
      const isSync = localStorage.getItem('his-sync-old')

      if (!isSync) {
        const oldActiveList = JSON.parse(localStorage.getItem('his-tracking') || '{}').data || []
        const oldArchiveList = JSON.parse(localStorage.getItem('his-archived') || '{}').data || []
        try {
          localStorage.setItem('his-sync-old', 'true')
        } catch (error) {
          console.log(error)
        }
        if (oldActiveList.length) {
          // console.log('兼容老版本 - 激活数据', oldActiveList)
          const localOldActiveList = oldActiveList.filter((item: any) => !item.FTrackInfoId && !activeList.find((newItem: any) => item.no === newItem.num))
          if (localOldActiveList && localOldActiveList.length) {
            localOldActiveList.forEach((item: any) => {
              activeList.push({num: item.no})
            })
            try {
              localStorage.setItem(TRACK_LIST_KEY, JSON.stringify(activeList))
            } catch (error) {
              console.log(error)
            }
          }
        }
        if (oldArchiveList.length) {
          // console.log('兼容老版本 - 归档数据')
          const localOldArchive = oldArchiveList.filter((item: any) => !item.FTrackInfoId && !archiveList.find((newItem: any) => item.no === newItem.num))
          // console.log('兼容合并数据 - 归档数据', localOldArchive)
          if (localOldArchive && localOldArchive.length) {
            localOldArchive.forEach((item: any) => {
              const time = item.trackTime ? new Date(item.trackTime) : new Date()
              archiveList.push({
                num: item.no,
                time: utils.tools.formatTime(time)
              })
            })
            try {
              localStorage.setItem(TRACK_LIST_ARCHIVE_KEY, JSON.stringify(archiveList))
            } catch (error) {
              console.log(error)
            }
          }
        }
      }
      
      //  ****** 兼容老版本数据 End ************* 
      state.activeList = activeList
      state.archiveList = archiveList
      
    },
    //  ------------------------ 激活列表操作start -------------------------------
    // 新增
    addActiveItem: (state, action: PayloadAction<Item>) => {
      const item = action.payload
      state.activeList.unshift(item)
      const list = clearRepatData(state.activeList)
      state.activeList = list
      setLocalStorage(TRACK_LIST_KEY, state.activeList)
    },
    // 删除
    deleteActiveItem: (state, action: PayloadAction<string>) => {
      const num = action.payload
      state.activeList = state.activeList.filter((item: Item) => item.num !== num)
      setLocalStorage(TRACK_LIST_KEY, state.activeList)
    },
    // 清空
    clearActiveList: (state) => {
      state.activeList = []
      setLocalStorage(TRACK_LIST_KEY, state.activeList)
    },
    // 存储查询数据信息
    setActiveQueryList: (state, action: PayloadAction<Array<any>>) => {
      state.activeQueryList = action.payload || []
    },
    //  ------------------------ 激活列表操作end ---------------------------------

    //  ------------------------ 归档列表操作start -------------------------------
    // 新增
    addArchiveItem: (state, action: PayloadAction<Item>) => {
      const item = action.payload
      state.archiveList.unshift(item)
      const list = clearRepatData(state.archiveList)
      state.archiveList = list
      setLocalStorage(TRACK_LIST_ARCHIVE_KEY, state.archiveList)
    },
    // 删除
    deleteArchiveItem: (state, action: PayloadAction<string>) => {
      const num = action.payload
      state.archiveList = state.archiveList.filter((item: Item) => item.num !== num)
      setLocalStorage(TRACK_LIST_ARCHIVE_KEY, state.archiveList)
    },
    // 清空
    clearArchiveList: (state) => {
      state.archiveList = []
      setLocalStorage(TRACK_LIST_ARCHIVE_KEY, state.archiveList)
    }
    //  ------------------------ 归档列表操作end ---------------------------------
  }
})

export const { 
  initLocalData,
  addActiveItem, deleteActiveItem, clearActiveList,setActiveQueryList,
  addArchiveItem, deleteArchiveItem, clearArchiveList
 } = trackLocalSlice.actions

export const getActiveList = (state: AppState) => state.trackLocal.activeList

export const getActiveQueryList = (state: AppState) => state.trackLocal.activeQueryList

export const getArchiveList = (state: AppState) => state.trackLocal.archiveList


export default trackLocalSlice.reducer
